// hooks/useUIState.ts
// Migrated to use typedApi for type safety.
import { useState, useEffect, useCallback } from 'react';
import { typedApi } from '../services/typedApiClient';
import type { SemestersMap } from '../types';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Collapsed semester states - keyed by semester code
 */
export interface CollapsedSemestersState {
  [semesterCode: string]: boolean;
}

/**
 * Academic plan type for UI state initialization. Kept narrow on purpose:
 * an index signature here would prevent passing well-typed shapes like
 * AcademicPlan (which doesn't declare one).
 */
export interface AcademicPlanForUI {
  semesters: SemestersMap;
}

/**
 * UI operations for controlling the interface
 */
export interface UIOperations {
  toggleSemester: (semester: string) => void;
  expandSemester: (semester: string) => void;
  collapseSemester: (semester: string) => void;
  expandAllSemesters: () => void;
  collapseAllSemesters: () => void;
  collapseEmptySemesters: (academicPlan: AcademicPlanForUI) => void;
  toggleBookmarks: () => void;
  expandBookmarks: () => void;
  setShowBookmarkedCourses: (visible: boolean) => void;
  hideBookmarkedCourses: () => void;
  showBookmarkedCoursesSection: () => void;
  openDropdown: (dropdownId: string | null) => void;
  closeDropdown: () => void;
  toggleDropdown: (dropdownId: string) => void;
  isDropdownOpen: (dropdownId: string) => boolean;
}

/**
 * Return type for the useUIState hook
 */
export interface UseUIStateReturn {
  collapsedSemesters: CollapsedSemestersState;
  bookmarkedCollapsed: boolean;
  showBookmarkedCourses: boolean;
  openDropdownId: string | null;
  uiOps: UIOperations;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Determine the default collapsed state for a single semester.
 * AP/Other Credits and Winter/Summer semesters default to collapsed.
 */
const shouldDefaultCollapsed = (semesterCode: string): boolean => {
  if (semesterCode === '000008' || semesterCode === '000009') return true;
  const semesterType = semesterCode.split('-')[0];
  return semesterType === 'winter' || semesterType === 'summer';
};

/**
 * Helper function to initialize collapsed states based on semester type
 */
const initializeCollapsedStates = (semesters: SemestersMap): CollapsedSemestersState => {
  const collapsed: CollapsedSemestersState = {};
  Object.keys(semesters).forEach(semester => {
    collapsed[semester] = shouldDefaultCollapsed(semester);
  });
  return collapsed;
};

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook to manage UI state preferences with localStorage persistence
 */
export const useUIState = (academicPlan: AcademicPlanForUI): UseUIStateReturn => {
  // Collapsed Semesters State
  const [collapsedSemesters, setCollapsedSemesters] = useState<CollapsedSemestersState>(() => {
    const savedStates = localStorage.getItem('collapsedSemesters');

    if (savedStates) {
      try {
        const parsed = JSON.parse(savedStates) as CollapsedSemestersState;

        // Migration: Add states for any missing semesters
        let needsUpdate = false;
        Object.keys(academicPlan.semesters).forEach(semester => {
          if (!(semester in parsed)) {
            parsed[semester] = shouldDefaultCollapsed(semester);
            needsUpdate = true;
          }
        });

        if (needsUpdate) {
          localStorage.setItem('collapsedSemesters', JSON.stringify(parsed));
        }

        return parsed;
      } catch {
        localStorage.removeItem('collapsedSemesters');
      }
    }

    // First time: generate default states
    const defaultStates = initializeCollapsedStates(academicPlan.semesters);
    localStorage.setItem('collapsedSemesters', JSON.stringify(defaultStates));
    return defaultStates;
  });

  // Bookmarked Section State
  const [bookmarkedCollapsed, setBookmarkedCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem('bookmarkedCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Bookmarked Courses Visibility State (persisted to backend)
  const [showBookmarkedCourses, setShowBookmarkedCoursesState] = useState<boolean>(() => {
    const saved = localStorage.getItem('showBookmarkedCourses');
    return saved ? JSON.parse(saved) : true;
  });

  // Dropdown State (single dropdown policy)
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Persist collapsed semesters to localStorage
  useEffect(() => {
    localStorage.setItem('collapsedSemesters', JSON.stringify(collapsedSemesters));
  }, [collapsedSemesters]);

  // Persist bookmarked collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('bookmarkedCollapsed', JSON.stringify(bookmarkedCollapsed));
  }, [bookmarkedCollapsed]);

  // Persist showBookmarkedCourses state to localStorage
  useEffect(() => {
    localStorage.setItem('showBookmarkedCourses', JSON.stringify(showBookmarkedCourses));
  }, [showBookmarkedCourses]);

  // Save bookmarked courses visibility to backend
  const saveBookmarkedCoursesVisibility = useCallback(async (visible: boolean): Promise<void> => {
    try {
      // Type assertion needed as PATCH endpoint may not be fully typed
      await typedApi.studentProfile.updatePreferences({ show_bookmarked_courses: visible } as never);
    } catch (error) {
      console.error('Failed to save bookmarked courses visibility:', error);
    }
  }, []);

  // UI Operations
  const uiOps: UIOperations = {
    // Semester collapse/expand
    toggleSemester: (semester: string) => {
      setCollapsedSemesters(prev => ({
        ...prev,
        [semester]: !prev[semester]
      }));
    },

    expandSemester: (semester: string) => {
      setCollapsedSemesters(prev => ({
        ...prev,
        [semester]: false
      }));
    },

    collapseSemester: (semester: string) => {
      setCollapsedSemesters(prev => ({
        ...prev,
        [semester]: true
      }));
    },

    // Bulk semester operations
    expandAllSemesters: () => {
      setCollapsedSemesters(prev => {
        const newState: CollapsedSemestersState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = false;
        });
        return newState;
      });
    },

    collapseAllSemesters: () => {
      setCollapsedSemesters(prev => {
        const newState: CollapsedSemestersState = {};
        Object.keys(prev).forEach(key => {
          newState[key] = true;
        });
        return newState;
      });
    },

    collapseEmptySemesters: (plan: AcademicPlanForUI) => {
      setCollapsedSemesters(prev => {
        const newState = { ...prev };
        Object.keys(plan.semesters).forEach(semester => {
          if (plan.semesters[semester].length === 0) {
            newState[semester] = true;
          }
        });
        return newState;
      });
    },

    // Bookmarked section
    toggleBookmarks: () => {
      setBookmarkedCollapsed(prev => !prev);
    },

    expandBookmarks: () => {
      setBookmarkedCollapsed(false);
    },

    // Bookmarked courses visibility (controls whether section is shown at all)
    setShowBookmarkedCourses: (visible: boolean) => {
      setShowBookmarkedCoursesState(visible);
      saveBookmarkedCoursesVisibility(visible);
    },

    hideBookmarkedCourses: () => {
      setShowBookmarkedCoursesState(false);
      saveBookmarkedCoursesVisibility(false);
    },

    showBookmarkedCoursesSection: () => {
      setShowBookmarkedCoursesState(true);
      saveBookmarkedCoursesVisibility(true);
    },

    // Dropdown management (enforces single dropdown policy)
    openDropdown: (dropdownId: string | null) => {
      setOpenDropdownId(dropdownId);
    },

    closeDropdown: () => {
      setOpenDropdownId(null);
    },

    toggleDropdown: (dropdownId: string) => {
      setOpenDropdownId(prev => prev === dropdownId ? null : dropdownId);
    },

    // Utility to check if a specific dropdown is open
    isDropdownOpen: (dropdownId: string) => openDropdownId === dropdownId
  };

  return {
    // UI State
    collapsedSemesters,
    bookmarkedCollapsed,
    showBookmarkedCourses,
    openDropdownId,

    // Operations
    uiOps
  };
};
